import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SCROLL_FETCH_MARGIN_PX } from '../config/constants';

/**
 * One paging engine for every list in the app.
 *
 * Six surfaces had grown six copies of this, and only one of them — `useSharedMedia`,
 * whose header documents the post-mortem — was correct. The other five shared the same
 * four faults, and this hook exists so there is one place they can be wrong:
 *
 * 1. **The in-flight latch was redux state.** Redux lags the event stream by a render
 *    and `scroll` fires many times per gesture, so every event in the first frame read
 *    `loading === false` and dispatched. The latch here is a ref, set synchronously, so
 *    the second event in a frame sees the first one's decision.
 *
 * 2. **The cursor lived in React state and was read from the handler's closure**, so
 *    those same events all asked for the SAME page. It is a ref here for the same
 *    reason, and it advances before the request goes out rather than after it returns.
 *
 * 3. **The stop condition was `loaded >= count`.** A count and a page are two reads of
 *    a table that is still being written to. When the server reports a count it does
 *    not actually serve, `loaded` stops climbing while `count` stays ahead of it and
 *    the guard can never hold — so every scroll to the end re-requests a page that adds
 *    nothing, forever. Exhaustion here is a *short page* (or an explicit null cursor),
 *    which is a property of the response rather than of two responses compared.
 *
 * 4. **Opening a panel always refetched page one.** `resetKey` marks which collection
 *    is loaded, so re-rendering with the same key shows what is already held.
 *
 * Triggering is an `IntersectionObserver` on a sentinel element rather than an
 * `onScroll` prop. `onScroll` fires on every scroll frame on the main thread and each
 * handler forced a layout read; the observer fires only when the end of the list is
 * actually approached, off the main thread, and it fires on MOUNT too — which is what
 * fixes the case where the first page does not fill the container and no scroll is
 * therefore possible.
 *
 * @param {object}   options
 * @param {any}      options.resetKey     identity of the collection; changing it rewinds paging
 * @param {Function} options.fetchPage    `(cursor|null) => void` — dispatch one page
 * @param {boolean}  options.loading      the owning slice's in-flight flag
 * @param {number}   options.loadedCount  how many rows are currently held
 * @param {string|null} [options.nextCursor]  server cursor; `null` after a load means exhausted
 * @param {boolean}  [options.enabled]    false parks the hook (e.g. a closed panel)
 * @param {'end'|'start'} [options.edge]  which edge to watch — `start` for message streams,
 *                                        which page backwards into older history
 * @param {any} [options.loadedFor]  which collection `loadedCount`/`nextCursor` describe.
 *                                   Pass it where responses for several collections land
 *                                   in one slice — a late page for the conversation just
 *                                   navigated away from is otherwise indistinguishable
 *                                   from this one's. Omit it and no attribution is done.
 */
const UNATTRIBUTED = Symbol('no collection identity supplied');

export const useInfiniteList = ({
  resetKey,
  fetchPage,
  loading,
  loadedCount = 0,
  nextCursor,
  enabled = true,
  edge = 'end',
  loadedFor = UNATTRIBUTED,
}) => {
  /** A request is out. Set synchronously, so a burst of events sees it. */
  const pending = useRef(false);
  /** Cursor the NEXT page starts from; null means "from the beginning". */
  const cursor = useRef(null);
  /** The server has no more to give, whatever any count reports. */
  const exhausted = useRef(false);
  /** Rows held when the in-flight request went out, to detect a short page. */
  const countAtRequest = useRef(0);
  /** Collection whose first page is already in the store. */
  const loadedKey = useRef(null);
  /** Node the observer watches. */
  const sentinel = useRef(null);

  // Re-render only where the caller needs it — `exhausted` drives an end-of-list
  // marker, and a ref alone would not repaint one.
  const [isExhausted, setIsExhausted] = useState(false);

  const rewind = useCallback(() => {
    loadedKey.current = null;
    cursor.current = null;
    exhausted.current = false;
    pending.current = false;
    countAtRequest.current = 0;
    setIsExhausted(false);
  }, []);

  // Declared before the effect that loads, so on a key change the rewind runs first and
  // the load below is what leaves its mark. Effects fire in declaration order within a
  // commit.
  useEffect(() => {
    rewind();
  }, [resetKey, rewind]);

  const loadPage = useCallback(
    (from) => {
      pending.current = true;
      /**
       * The baseline the short-page test measures against.
       *
       * Zero for a cursor-less read, because that read REPLACES the collection — the
       * rows held right now are the previous one's and are not part of what this
       * request will return. Taking `loadedCount` there was wrong whenever a switch
       * had not emptied the store yet, which is the normal case: the selection changes
       * one commit before the clear. A 20-row conversation followed by another 20-row
       * conversation measured as "added 0 rows", so the very first page was ruled the
       * last page — and with `exhausted` set, the sentinel unmounts and scrolling up
       * asks for nothing for the rest of the visit.
       */
      countAtRequest.current = from == null ? 0 : loadedCount;
      fetchPage(from);
    },
    [fetchPage, loadedCount]
  );

  /**
   * Settle the latch when a request finishes, and decide whether that was the last page.
   *
   * DECLARED BEFORE THE EFFECT THAT DISPATCHES, and that is the whole of it. Effects run
   * in declaration order within a commit, and `loading` comes from redux — it is read
   * from the render closure, so it cannot reflect a dispatch made by a later effect in
   * the same commit. Sitting after the load effect, this ran in the dispatch's own
   * commit and saw exactly what a finished request looks like: `pending` true (set
   * synchronously a moment earlier) and `loading` false (a render behind). It settled
   * the request that had just gone out, measured "added 0 rows", and marked the
   * collection exhausted — on the FIRST page, before any response existed.
   *
   * That unmounts the sentinel, so scrolling up asked for nothing ever again. Ahead of
   * the load effect, `pending` is still false in that commit and there is nothing to
   * settle; the earliest this can act is the commit the response actually lands in.
   *
   * Two independent exhaustion signals, because not every endpoint has been migrated to
   * cursors yet:
   *   • an explicit `next_cursor: null` from a cursor endpoint, and
   *   • a page that added fewer rows than a full one, for the offset endpoints.
   * Either is conclusive on its own.
   */
  useEffect(() => {
    if (loading || !pending.current) return;

    /**
     * What landed belongs to a different collection.
     *
     * Switching conversation while a page is in flight does not cancel it — the request
     * completes and writes into the same slice. Settling this conversation's request
     * against it hands the pager the PREVIOUS conversation's cursor, and then swallows
     * the real page when it arrives, because the latch has already been released. The
     * result is a conversation whose scroll-up asks for someone else's history, or for
     * nothing at all.
     *
     * Leaving `pending` set is deliberate: the request really is still out.
     */
    if (loadedFor !== UNATTRIBUTED && String(loadedFor) !== String(resetKey)) return;

    pending.current = false;

    const added = (loadedCount ?? 0) - countAtRequest.current;
    const serverSaysDone = nextCursor === null || nextCursor === undefined;
    // `added === 0` is only exhaustion when the request actually landed — which it did,
    // because `pending` was set and `loading` has now gone false.
    if (serverSaysDone || added <= 0) {
      exhausted.current = true;
      setIsExhausted(true);
    } else {
      cursor.current = nextCursor;
    }
  }, [loading, loadedCount, nextCursor, loadedFor, resetKey]);

  // First page. Guarded on the key rather than on emptiness: an empty collection is a
  // legitimate result, and re-requesting page one because it came back empty is how a
  // list with nothing in it turns into a request loop.
  useEffect(() => {
    if (!enabled) return;
    if (loadedKey.current === resetKey) return;

    loadedKey.current = resetKey;
    loadPage(null);
    // `loadPage` is deliberately absent from the dependency list: it changes identity
    // whenever `loadedCount` does, which is on every page — and re-running this effect
    // then would re-request page one on top of the list it had just extended. The
    // `loadedKey` guard above makes a spurious re-run harmless regardless.
  }, [enabled, resetKey, loadPage]);

  const loadMore = useCallback(() => {
    if (!enabled || pending.current || exhausted.current) return;
    // Nothing has landed yet — the first-page effect owns that request, and firing a
    // second one here would ask for the same page with the same null cursor.
    if (loadedKey.current === null) return;
    loadPage(cursor.current);
  }, [enabled, loadPage]);

  // `useLayoutEffect` so the observer is attached in the same commit the sentinel is
  // painted in. Under `useEffect` a sentinel already inside the viewport (a first page
  // too short to fill the pane) could be missed entirely.
  useLayoutEffect(() => {
    const node = sentinel.current;
    if (!node || !enabled) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      {
        root: node.closest('[data-infinite-root]') ?? null,
        // Ask before the reader arrives, and on the correct side: a message stream
        // pages backwards into older history, so its sentinel sits at the TOP.
        rootMargin:
          edge === 'start'
            ? `${SCROLL_FETCH_MARGIN_PX}px 0px 0px 0px`
            : `0px 0px ${SCROLL_FETCH_MARGIN_PX}px 0px`,
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore, enabled, edge]);

  return {
    /** Attach to an empty element at the paging edge of the list. */
    sentinelRef: sentinel,
    loadMore,
    rewind,
    exhausted: isExhausted,
    /** Only true for a request that is actually out — the slice flag is also true at boot. */
    loading: Boolean(loading) && pending.current,
  };
};

/**
 * Holds a prepending list still.
 *
 * When older messages are prepended, `scrollHeight` grows while `scrollTop` does not, so
 * the viewport lands somewhere else in the conversation and the reader loses their
 * place. Worse, the container stays pinned near the top, which re-triggers the load.
 *
 * `capture()` before the rows change, `restore()` in the layout phase after — restoring
 * before paint, so the jump is never displayed rather than corrected afterwards.
 */
export const useScrollAnchor = (containerRef, dependency) => {
  const anchor = useRef(null);

  const capture = useCallback(() => {
    const node = containerRef.current;
    if (!node) return;
    anchor.current = { height: node.scrollHeight, top: node.scrollTop };
  }, [containerRef]);

  useLayoutEffect(() => {
    const node = containerRef.current;
    const previous = anchor.current;
    if (!node || !previous) return;

    const grown = node.scrollHeight - previous.height;
    // Only compensate growth at the head. A message ARRIVING at the tail also grows
    // scrollHeight, and holding position for that would fight the scroll-to-bottom.
    if (grown > 0) node.scrollTop = previous.top + grown;
    anchor.current = null;
  }, [containerRef, dependency]);

  return capture;
};

/**
 * Opens a message stream at its newest message.
 *
 * A stream renders oldest-first and is scrolled from the top, so a freshly loaded
 * channel sits at `scrollTop: 0` — the OLDEST message of the newest page. That is
 * what "it opens on last week's messages, today's are further down" was.
 *
 * Nothing here used to own that. The direct pane relied on `useScrollAnchor`
 * happening to compensate the first page's growth from an empty container, which
 * works on a fresh mount and not otherwise: switching conversations dispatches a
 * clear, the clear SHRINKS the container, and the anchor deliberately ignores
 * shrinkage and then discards itself — so the page that arrived next was never
 * compensated and the reader landed at the top. The group pane did own it, but
 * behind a ref that was never reset per group, so it fired for the first group
 * opened in a session and no other.
 *
 * Keyed on the conversation, so every open gets one pin and a prepended page —
 * which must hold position, not jump — gets none.
 *
 * Media has no height until it loads, so `scrollHeight` keeps growing after this
 * commit. `load` does not bubble, but a capture-phase listener on the container
 * still sees it, which re-pins for images, video and audio without polling. The
 * window closes on the first sign the reader is scrolling for themselves,
 * because yanking them back to the bottom would be worse than a short stream.
 *
 * @param {object} containerRef  ref to the scroll container
 * @param {object} options
 * @param {any}    options.key       conversation identity; a change re-arms the pin
 * @param {number} options.count     rows currently rendered; the pin waits for the first
 * @param {number} [options.settleMs] how long to keep re-pinning for late media
 */
export const useStreamBottomOnOpen = (containerRef, { key, count, settleMs = 1500 }) => {
  const pinnedFor = useRef({ key: null, node: null });

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node || key == null || !count) return undefined;
    // The node as well as the key: a panel that unmounts and reopens on the SAME
    // conversation — a thread — comes back as a fresh element scrolled to the top, and
    // keying on the conversation alone would treat that as already pinned.
    if (pinnedFor.current.key === key && pinnedFor.current.node === node) return undefined;

    pinnedFor.current = { key, node };

    const pin = () => {
      node.scrollTop = node.scrollHeight;
    };
    pin();

    let timer = null;
    const release = () => {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      node.removeEventListener('load', pin, true);
      node.removeEventListener('wheel', release);
      node.removeEventListener('touchstart', release);
      node.removeEventListener('keydown', release);
    };

    node.addEventListener('load', pin, true);
    node.addEventListener('wheel', release, { passive: true });
    node.addEventListener('touchstart', release, { passive: true });
    node.addEventListener('keydown', release);
    timer = setTimeout(release, settleMs);

    return release;
  }, [containerRef, key, count, settleMs]);
};

export default useInfiniteList;
